import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, ExternalLink, X } from 'lucide-react';
import { apiFetch } from '../utils/api';
import { formatAmount } from '../utils/formatters';
import { useI18n } from '../hooks/useI18n';

interface Invoice {
    id: string;
    invoice_number: string;
    vendor: {
        name: string;
    } | null;
    cost_center: {
        name: string;
    } | null;
    total_amount: number;
    invoice_date: string;
    status: string;
}

interface BankTransactionAsInvoice {
    id: string;
    invoice_number: string | null;
    vendor: {
        name: string;
    } | null;
    cost_center: {
        name: string;
    } | null;
    total_amount: number;
    invoice_date: string;
    status: string;
}

interface CostReportInvoicesModalProps {
    isOpen: boolean;
    onClose: () => void;
    categoryName: string;
    costCenterName: string;
    categoryId: string;
    costCenterId: string;
    year: number;
    month: number;
    baseCurrency: string;
}

export default function CostReportInvoicesModal({
    isOpen,
    onClose,
    categoryName,
    costCenterName,
    categoryId,
    costCenterId,
    year,
    month,
    baseCurrency
}: CostReportInvoicesModalProps) {
    const { t } = useI18n();
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [bankTransactions, setBankTransactions] = useState<BankTransactionAsInvoice[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const fetchInvoices = async () => {
        if (!isOpen || !costCenterId) return;

        setLoading(true);
        setError(null);

        try {
            // Build URL with conditional category_id parameter
            let url = `/api/cost-reports/monthly/transactions?year=${year}&month=${month}&cost_center_id=${costCenterId}`;
            if (categoryId && categoryId !== 'null') {
                url += `&category_id=${categoryId}`;
            }

            const response = await apiFetch(url);

            if (!response.ok) {
                throw new Error(`Failed to fetch transactions: ${response.status}`);
            }

            const data = await response.json();
            setInvoices(data.invoices || []);
            setBankTransactions(data.bank_transactions || []);
        } catch (err) {
            console.error('Error fetching transactions:', err);
            setError(err instanceof Error ? err.message : 'An unknown error occurred');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInvoices();
    }, [isOpen, categoryId, costCenterId, year, month]);

    // Handle ESC key to close modal
    useEffect(() => {
        const handleEscKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscKey);
        }

        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [isOpen, onClose]);

    const getStatusBadgeVariant = (status: string) => {
        switch (status) {
            case 'approved':
                return 'default';
            case 'verified':
                return 'secondary';
            case 'rejected':
                return 'destructive';
            default:
                return 'outline';
        }
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString();
    };

    const handleViewInvoice = (invoiceId: string) => {
        // Open invoice in new tab
        window.open(`/invoices/${invoiceId}`, '_blank');
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
            onClick={onClose}
        >
            <div
                className="bg-background border rounded-lg shadow-lg max-w-4xl w-full max-h-[80vh] overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b">
                    <div>
                        <h2 className="text-lg font-semibold">
                            {t('costReports.invoicesFor', 'finance')} {categoryName} - {costCenterName}
                        </h2>
                        <p className="text-sm text-muted-foreground mt-1">
                            {t('costReports.showingContributedInvoices', 'finance')} {new Date(year, month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}.
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                            {t('costReports.netAmountsNote', 'finance')}
                        </p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={onClose}>
                        <X className="h-4 w-4" />
                    </Button>
                </div>

                {/* Body */}
                <div className="p-6 overflow-y-auto max-h-[60vh]">
                    {error && (
                        <Alert variant="destructive" className="mb-4">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>Error: {error}</AlertDescription>
                        </Alert>
                    )}

                    {loading ? (
                        <div className="space-y-3">
                            {[...Array(5)].map((_, i) => (
                                <div key={i} className="flex space-x-4">
                                    <Skeleton className="h-4 w-24" />
                                    <Skeleton className="h-4 w-32" />
                                    <Skeleton className="h-4 w-24" />
                                    <Skeleton className="h-4 w-20" />
                                    <Skeleton className="h-4 w-16" />
                                </div>
                            ))}
                        </div>
                    ) : invoices.length === 0 && bankTransactions.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                            {t('costReports.noInvoicesFound', 'finance')}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>{t('invoices.invoiceNumber', 'invoices')}</TableHead>
                                        <TableHead>{t('common.vendor', 'common')}</TableHead>
                                        <TableHead>{t('forms.date', 'common')}</TableHead>
                                        <TableHead className="text-right">{t('units.total', 'common')}</TableHead>
                                        <TableHead>{t('forms.status', 'common')}</TableHead>
                                        <TableHead className="w-16"></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {/* Regular Invoices */}
                                    {invoices.map((invoice) => (
                                        <TableRow key={invoice.id}>
                                            <TableCell className="font-medium">
                                                {invoice.invoice_number || '-'}
                                            </TableCell>
                                            <TableCell>
                                                {invoice.vendor?.name || '-'}
                                            </TableCell>
                                            <TableCell>
                                                {formatDate(invoice.invoice_date)}
                                            </TableCell>
                                            <TableCell className="text-right font-medium">
                                                {formatAmount(invoice.total_amount, { currencyCode: baseCurrency })}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant={getStatusBadgeVariant(invoice.status)}>
                                                    {invoice.status}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleViewInvoice(invoice.id)}
                                                    className="h-8 w-8 p-0"
                                                >
                                                    <ExternalLink className="h-4 w-4" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}

                                    {/* Bank Transactions (displayed as invoice-like rows) */}
                                    {bankTransactions.map((bankTx) => (
                                        <TableRow key={bankTx.id} className="bg-blue-50/30">
                                            <TableCell className="font-medium text-muted-foreground">
                                                -
                                            </TableCell>
                                            <TableCell>
                                                {bankTx.vendor?.name || '-'}
                                            </TableCell>
                                            <TableCell>
                                                {formatDate(bankTx.invoice_date)}
                                            </TableCell>
                                            <TableCell className="text-right font-medium">
                                                {formatAmount(bankTx.total_amount, { currencyCode: baseCurrency })}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300">
                                                    {bankTx.status}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                {/* No action button for bank transactions */}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-end p-6 border-t bg-muted/50">
                    <Button variant="outline" onClick={onClose}>
                        {t('actions.close', 'common')}
                    </Button>
                </div>
            </div>
        </div>
    );
}
